import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiCloud,
  FiRefreshCw,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiActivity,
  FiClock,
  FiZap,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import { getBedrockQuotaStatus, getCredentials } from '../../../../services/aiOperationsApi';

interface QuotaBarProps {
  label: string;
  used: number;
  limit: number;
  unit?: string;
}

const QuotaBar: React.FC<QuotaBarProps> = ({ label, used, limit, unit = '' }) => {
  const percentage = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;

  const getBarColor = (): string => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="text-gray-500 dark:text-gray-500">
          {used.toLocaleString()}
          {unit} / {limit.toLocaleString()}
          {unit}
        </span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className={`${getBarColor()} h-2 rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

type BannerStatus = 'healthy' | 'warning' | 'critical' | 'error' | 'unavailable' | 'unknown';

interface StatusConfig {
  icon: IconType;
  bg: string;
  text: string;
  label: string;
}

const STATUS_CONFIG: Record<BannerStatus, StatusConfig> = {
  healthy: {
    icon: FiCheckCircle,
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    label: 'Healthy',
  },
  warning: {
    icon: FiAlertTriangle,
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    label: 'Warning',
  },
  critical: {
    icon: FiXCircle,
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    label: 'Critical',
  },
  error: {
    icon: FiXCircle,
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    label: 'Error',
  },
  unavailable: {
    icon: FiActivity,
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-600 dark:text-gray-400',
    label: 'Unavailable',
  },
  unknown: {
    icon: FiActivity,
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-600 dark:text-gray-400',
    label: 'Unknown',
  },
};

interface StatusBadgeProps {
  status: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const cfg = STATUS_CONFIG[status as BannerStatus] ?? STATUS_CONFIG.unknown;
  const Icon = cfg.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

interface BedrockCredential {
  id: string;
  provider: string;
  is_enabled: boolean;
}

interface QuotaItem {
  quota_name: string;
  value: number;
  unit?: string;
}

interface UsageHourly {
  successful_requests: number;
  throttled_requests: number;
  input_tokens: number;
  output_tokens: number;
}

interface UsageData {
  last_hour?: UsageHourly;
  last_24h?: Partial<UsageHourly>;
}

interface QuotaData {
  status: string;
  message?: string;
  error?: string;
  quotas?: QuotaItem[];
  usage?: UsageData;
}

interface UsageMetrics {
  requestsPerMinute: number;
  tokensPerMinute: number;
  hourlyRequests: number;
  hourlyThrottled: number;
  hourlyInputTokens: number;
  hourlyOutputTokens: number;
  dailyRequests: number;
  dailyThrottled: number;
}

interface BedrockQuotaBannerProps {
  hasActiveEvaluations?: boolean;
  refreshInterval?: number;
  compact?: boolean;
}

const BedrockQuotaBanner: React.FC<BedrockQuotaBannerProps> = ({
  hasActiveEvaluations = false,
  refreshInterval = 60000,
  compact = false,
}) => {
  const [quotaData, setQuotaData] = useState<QuotaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [bedrockCredential, setBedrockCredential] = useState<BedrockCredential | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    const findBedrockCredential = async () => {
      try {
        const credentials = await getCredentials();
        const bedrockCred = credentials.find(
          (c: BedrockCredential) => c.provider === 'bedrock' && c.is_enabled
        );
        setBedrockCredential(bedrockCred ?? null);
      } catch (err: unknown) {
        console.error('Failed to find Bedrock credential:', err);
        setBedrockCredential(null);
      }
    };
    findBedrockCredential();
  }, []);

  const fetchQuota = useCallback(async () => {
    if (!bedrockCredential?.id) return;

    const isInitialFetch = !quotaData && !error;
    if (isInitialFetch) {
      setLoading(true);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    try {
      const data = await getBedrockQuotaStatus(bedrockCredential.id, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      setQuotaData(data);
      setLastUpdated(new Date());

      if (['error', 'unavailable'].includes(data.status)) {
        setError(data.message ?? data.error ?? 'Quota information unavailable');
      } else {
        setError(null);
      }
    } catch (err: unknown) {
      clearTimeout(timeoutId);
      console.error('Failed to fetch Bedrock quota:', err);

      const isTimeout =
        (err instanceof Error && err.name === 'AbortError') ||
        (err instanceof Error &&
          (err.message?.toLowerCase().includes('timeout') ||
            err.message?.toLowerCase().includes('aborted')));

      if (isTimeout) {
        setError('Quota check timed out. Will retry automatically.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load quota information');
      }
    } finally {
      setLoading(false);
    }
  }, [bedrockCredential?.id, quotaData, error]);

  useEffect(() => {
    if (bedrockCredential?.id) {
      fetchQuota();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bedrockCredential?.id]);

  useEffect(() => {
    if (!bedrockCredential?.id) return;

    const interval = hasActiveEvaluations ? refreshInterval : refreshInterval * 2;
    const timer = setInterval(fetchQuota, interval);
    return () => clearInterval(timer);
  }, [bedrockCredential?.id, hasActiveEvaluations, refreshInterval, fetchQuota]);

  const usageMetrics = useMemo((): UsageMetrics | null => {
    if (!quotaData?.usage?.last_hour) return null;

    const hourly = quotaData.usage.last_hour;
    const daily = quotaData.usage.last_24h ?? {};

    const requestsPerMinute = hourly.successful_requests / 60;
    const tokensPerMinute = (hourly.input_tokens + hourly.output_tokens) / 60;

    return {
      requestsPerMinute: Math.round(requestsPerMinute * 10) / 10,
      tokensPerMinute: Math.round(tokensPerMinute),
      hourlyRequests: hourly.successful_requests,
      hourlyThrottled: hourly.throttled_requests,
      hourlyInputTokens: hourly.input_tokens,
      hourlyOutputTokens: hourly.output_tokens,
      dailyRequests: daily.successful_requests ?? 0,
      dailyThrottled: daily.throttled_requests ?? 0,
    };
  }, [quotaData]);

  const primaryQuota = useMemo((): QuotaItem | null => {
    if (!quotaData?.quotas?.length) return null;

    const invocationQuota = quotaData.quotas.find(
      (q) =>
        q.quota_name?.toLowerCase().includes('invocation') ||
        q.quota_name?.toLowerCase().includes('request')
    );

    return invocationQuota ?? quotaData.quotas[0];
  }, [quotaData]);

  if (!bedrockCredential) {
    return null;
  }

  if (loading && !quotaData) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 mb-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <FiCloud className="w-4 h-4 text-gray-400 animate-pulse" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse" />
          </div>
          <FiRefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error && !quotaData) {
    const isPermissionError =
      error.includes('AccessDenied') || error.includes('not authorized');
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <FiCloud className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                {isPermissionError
                  ? 'Quota monitoring requires additional permissions'
                  : 'Quota info unavailable'}
              </span>
            </div>
            {isPermissionError && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 ml-6">
                Add{' '}
                <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">
                  servicequotas:ListServiceQuotas
                </code>{' '}
                and{' '}
                <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">
                  cloudwatch:GetMetricData
                </code>{' '}
                to your IAM policy.{' '}
                <a
                  href="/settings/llm-providers"
                  className="underline hover:text-amber-800 dark:hover:text-amber-200"
                >
                  See Settings for details
                </a>
              </p>
            )}
          </div>
          <button
            onClick={fetchQuota}
            className="p-1 text-amber-600 hover:text-amber-700 dark:text-amber-400 flex-shrink-0"
          >
            <FiRefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (compact && quotaData) {
    let compactBg: string;
    if (quotaData.status === 'critical') compactBg = 'bg-red-50 dark:bg-red-900/20';
    else if (quotaData.status === 'warning') compactBg = 'bg-amber-50 dark:bg-amber-900/20';
    else compactBg = 'bg-blue-50 dark:bg-blue-900/20';

    return (
      <div className={`rounded-lg p-2 mb-3 flex items-center justify-between ${compactBg}`}>
        <div className="flex items-center gap-2">
          <FiCloud className="w-4 h-4 text-blue-500" />
          <span className="text-xs text-gray-600 dark:text-gray-400">
            Bedrock: {usageMetrics?.requestsPerMinute ?? 0} req/min
          </span>
          <StatusBadge status={quotaData.status} />
        </div>
        {quotaData.status !== 'healthy' && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{quotaData.message}</span>
        )}
      </div>
    );
  }

  let bannerBg: string;
  if (quotaData?.status === 'critical') bannerBg = 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
  else if (quotaData?.status === 'warning') bannerBg = 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
  else bannerBg = 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';

  return (
    <div className={`rounded-lg border mb-4 transition-colors ${bannerBg}`}>
      {/* Header */}
      <div
        className="p-3 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }} role="button" tabIndex={0}
      >
        <div className="flex items-center gap-3">
          <FiCloud className="w-5 h-5 text-blue-500" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white">Bedrock API Usage</span>
              <StatusBadge status={quotaData?.status ?? 'unknown'} />
            </div>
            {quotaData?.message && quotaData.status !== 'healthy' && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{quotaData.message}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {usageMetrics && !expanded && (
            <div className="hidden sm:flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <FiZap className="w-3 h-3" />
                {usageMetrics.requestsPerMinute} req/min
              </span>
              {usageMetrics.hourlyThrottled > 0 && (
                <span className="text-amber-600 dark:text-amber-400">
                  {usageMetrics.hourlyThrottled} throttled
                </span>
              )}
            </div>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchQuota();
            }}
            disabled={loading}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
            title="Refresh quota"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {expanded ? (
            <FiChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <FiChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 space-y-4 border-t border-gray-200 dark:border-gray-700 pt-3">
          {primaryQuota && usageMetrics && (
            <div className="space-y-3">
              <QuotaBar
                label="Requests/min"
                used={usageMetrics.requestsPerMinute}
                limit={primaryQuota.value || 100}
              />
              <QuotaBar
                label="Tokens/min"
                used={usageMetrics.tokensPerMinute}
                limit={40000}
              />
            </div>
          )}

          {usageMetrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-white dark:bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">Hourly Requests</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {usageMetrics.hourlyRequests.toLocaleString()}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">Throttled (1h)</p>
                <p
                  className={`font-semibold ${
                    usageMetrics.hourlyThrottled > 0
                      ? 'text-amber-600'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {usageMetrics.hourlyThrottled}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">Input Tokens (1h)</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {usageMetrics.hourlyInputTokens.toLocaleString()}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">Output Tokens (1h)</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {usageMetrics.hourlyOutputTokens.toLocaleString()}
                </p>
              </div>
            </div>
          )}

          {usageMetrics &&
            (usageMetrics.dailyRequests > 0 || usageMetrics.dailyThrottled > 0) && (
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                <span>Last 24h:</span>
                <span>{usageMetrics.dailyRequests.toLocaleString()} requests</span>
                {usageMetrics.dailyThrottled > 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {usageMetrics.dailyThrottled} throttled
                  </span>
                )}
              </div>
            )}

          {quotaData?.quotas && quotaData.quotas.length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <details className="cursor-pointer">
                <summary className="hover:text-gray-700 dark:hover:text-gray-300">
                  View {quotaData.quotas.length} quota limits
                </summary>
                <ul className="mt-2 space-y-1 pl-4">
                  {quotaData.quotas.slice(0, 5).map((quota) => (
                    <li key={quota.quota_name} className="flex justify-between">
                      <span className="truncate mr-2">{quota.quota_name}</span>
                      <span className="font-mono">
                        {quota.value} {quota.unit}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}

          {lastUpdated && (
            <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
              <FiClock className="w-3 h-3" />
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BedrockQuotaBanner;
